import React from "react";
import styled, { keyframes } from "styled-components";

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const SkeletonContainer = styled.div`
  min-height: 100vh;
  background: #fff;
`;

const SkeletonWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const SkeletonLine = styled.div`
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 1000px 100%;
  animation: ${shimmer} 2s infinite;
  border-radius: 4px;
  margin-bottom: ${(props) => props.marginBottom || "16px"};
  height: ${(props) => props.height || "16px"};
  width: ${(props) => props.width || "100%"};
`;

const SkeletonTitle = styled(SkeletonLine)`
  width: 200px;
  height: 32px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    width: 150px;
    height: 24px;
    margin-bottom: 30px;
  }
`;

const ProfileSectionSkeleton = styled.div`
  background: #f9f9f9;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 30px;
  margin-bottom: 40px;
  display: flex;
  align-items: center;
  gap: 30px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
    padding: 20px;
  }
`;

const SkeletonAvatar = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 1000px 100%;
  animation: ${shimmer} 2s infinite;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 80px;
    height: 80px;
  }
`;

const ProfileInfoSkeleton = styled.div`
  flex: 1;
`;

const SkeletonTextLines = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OptionsGridSkeleton = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 50px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const OptionCardSkeleton = styled.div`
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 24px;
  display: flex;
  gap: 16px;

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const SkeletonIconBox = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 8px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 1000px 100%;
  animation: ${shimmer} 2s infinite;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
  }
`;

const MyAccountSkeleton = () => {
  return (
    <SkeletonContainer>
      <SkeletonWrapper>
        <SkeletonTitle />

        <ProfileSectionSkeleton>
          <SkeletonAvatar />
          <ProfileInfoSkeleton>
            <SkeletonTextLines>
              <SkeletonLine width="50%" height="24px" />
              <SkeletonLine width="70%" height="16px" marginBottom="8px" />
              <SkeletonLine width="60%" height="16px" marginBottom="8px" />
              <SkeletonLine width="65%" height="16px" />
            </SkeletonTextLines>
          </ProfileInfoSkeleton>
        </ProfileSectionSkeleton>

        <OptionsGridSkeleton>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <OptionCardSkeleton key={i}>
              <SkeletonIconBox />
              <div style={{ flex: 1 }}>
                <SkeletonLine width="80%" height="20px" marginBottom="12px" />
                <SkeletonLine width="100%" height="16px" marginBottom="6px" />
                <SkeletonLine width="90%" height="16px" />
              </div>
            </OptionCardSkeleton>
          ))}
        </OptionsGridSkeleton>
      </SkeletonWrapper>
    </SkeletonContainer>
  );
};

export default MyAccountSkeleton;
